import { memo } from 'react';
import { Button } from '@/components/ui/button';
import { Clock, Eye, Fingerprint, MapPinOff, Rocket, Send, Unlock } from 'lucide-react';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { PrazoSlaBadge } from '@/components/kanban/PrazoSlaBadge';
import {
  CancelarItemTriagemButton,
  ContatoPedidoCell,
  TimelineCnpjButton,
  formatCnpjTriagem,
  resumoProdutos,
} from '@/components/kanban/triagem/triagemPedidoUtils';
import { pedidoEhFibra, pedidoSemBiometria, type DestinoSemBiometria, type PedidoTriagemCriacao } from './useTriagemCriacaoPedido';
import { TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO } from '@/components/kanban/triagem/triagemTabela';
import { AcoesTriagem, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';

interface TriagemCriacaoPedidoRowProps {
  pedido: PedidoTriagemCriacao;
  posicao: number;
  /** É a vez DESTA pessoa: o pedido dela mais antigo (ou o 1º livre, se ela não tem nenhum). */
  isTopo: boolean;
  /** O pedido é desta pessoa. */
  isMeu: boolean;
  isProcessing: boolean;
  /** Supervisora do Backoffice e gestão podem soltar pedido preso no nome de alguém. */
  podeLiberar: boolean;
  onEnviarBiometria: (pedido: PedidoTriagemCriacao) => void;
  onSemBiometria: (pedido: PedidoTriagemCriacao, destino: DestinoSemBiometria) => void;
  onManterFimFila: (pedido: PedidoTriagemCriacao) => void;
  onLiberar: (pedido: PedidoTriagemCriacao) => void;
  onPegar: (pedido: PedidoTriagemCriacao) => void;
  onAfterCancel: () => void;
}

export const TriagemCriacaoPedidoRow = memo(function TriagemCriacaoPedidoRow({
  pedido,
  posicao,
  isTopo,
  isMeu,
  isProcessing,
  podeLiberar,
  onEnviarBiometria,
  onSemBiometria,
  onManterFimFila,
  onLiberar,
  onPegar,
  onAfterCancel,
}: TriagemCriacaoPedidoRowProps) {
  const { open: openPedido } = usePainelPedido();

  /* LEI 18/08/2026, corrigida em 21/08: o pedido de OUTRO BKO fica travado com o
     nome de quem pegou. O que abre é o mais antigo da fila entre os meus e os
     livres — livre antigo passa na frente do meu, porque ele é quem está segurando
     a fila. Ver o comentário do `indiceLiberado` no Modal. */
  const bloqueadoTitle = pedido.dono_id && !isMeu
    ? `Pedido de ${pedido.dono_nome || 'outro BKO'} — trabalhe o seu`
    : !pedido.dono_id && isTopo
      ? 'Pegue o pedido antes de trabalhar nele'
      : 'Trabalhe primeiro o mais antigo da fila que está liberado para você';
  /* Livre e liberado para mim = ainda não é meu: só o botão de PEGAR aparece.
     Trabalhar (biometria, fim da fila, cancelar) é coisa de dono — senão dois
     BKO criam o mesmo contrato antes de qualquer um carimbar. */
  const podePegar = !pedido.dono_id && isTopo && !isProcessing;
  const acoesBloqueadas = !isTopo || !isMeu || isProcessing;
  const { resumo, completo } = resumoProdutos(pedido.itens);
  const qtdTotal = pedido.itens.reduce((sum, i) => sum + (i.quantidade || 0), 0);
  /* Os mesmos botões, no mesmo lugar, para todo mundo (Douglas 14/10/2026): o
     que não vale para este pedido fica apagado e diz o porquê, nunca some. */
  const semBiometria = pedidoSemBiometria(pedido);
  const ehFibra = pedidoEhFibra(pedido);

  /* §7 da régua: só o 1º da fila tem destaque — filete bordô POR DENTRO
     (inset 3px) + fundo quente. Os demais ficam neutros: `opacity` saiu porque
     de-ênfase é por COR, e opacity apagava junto o texto que precisa ser lido. */
  const rowClass = isTopo
    ? 'box-border h-[33px] border-b border-border/30 bg-accent/40 shadow-[inset_3px_0_0_hsl(var(--primary))] hover:bg-accent/70 transition-colors'
    : 'box-border h-[33px] border-b border-border/30 hover:bg-muted/30 transition-colors';

  return (
    <tr className={rowClass}>
      <td className="px-2 py-0 align-middle">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold tabular-nums whitespace-nowrap ${isTopo ? 'text-primary' : 'text-muted-foreground'}`}>
          {isTopo ? '▶' : null}
          {posicao + 1}º
          {pedido.manter_em && <Clock className="h-3 w-3 text-primary" aria-label="Passado para o fim da fila" />}
        </span>
      </td>

      <td className="px-2 py-0 align-middle overflow-hidden">
        <CopyOnClick value={pedido.documento || pedido.cnpj} className="tabular-nums text-xs whitespace-nowrap">
          {formatCnpjTriagem(pedido.documento || pedido.cnpj)}
        </CopyOnClick>
      </td>

      <td className="px-2 py-0 align-middle overflow-hidden">
        <span className="text-xs text-foreground truncate block" title={pedido.razao_social}>
          {pedido.razao_social || '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle overflow-hidden">
        <span className="text-xs text-muted-foreground truncate block" title={`${pedido.consultor_nome}${pedido.equipe_nome ? ` • ${pedido.equipe_nome}` : ''}`}>
          {pedido.consultor_nome || '-'}{pedido.equipe_nome ? ` • ${pedido.equipe_nome}` : ''}
        </span>
      </td>

      <td className="px-2 py-0 align-middle overflow-hidden">
        {pedido.dono_id ? (
          <span
            className={`text-xs truncate block ${isMeu ? 'font-bold text-primary' : 'text-muted-foreground'}`}
            title={isMeu ? 'Você pegou este pedido' : `Pegou: ${pedido.dono_nome}`}
          >
            {isMeu ? 'VOCÊ' : (pedido.dono_nome || '-')}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground" title="Ninguém pegou ainda — livre para quem estiver sem pedido">
            livre
          </span>
        )}
      </td>

      <td className="px-2 py-0 align-middle">
        <button
          onClick={(e) => { e.stopPropagation(); openPedido(pedido.pedido_id, 'visao360'); }}
          className="text-xs font-medium text-primary hover:underline cursor-pointer"
          title="Abrir Painel do Pedido para análise"
        >
          {pedido.numero_pedido}
        </button>
      </td>

      <td className="px-2 py-0 align-middle overflow-hidden">
        <span className="text-xs text-muted-foreground truncate block" title={completo}>
          {resumo || '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground">{qtdTotal || '-'}</span>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {pedido.valor_total ? `R$ ${pedido.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <PrazoSlaBadge pedidoId={pedido.pedido_id} coluna="CRIAÇÃO PEDIDO" entradaColunaEm={pedido.entrada_fila_criacao_em || pedido.entrada_coluna_em} prazoHoras={5} />
      </td>

      <td className="px-2 py-0 align-middle">
        <div className="flex items-center gap-2">
          <ContatoPedidoCell telefone={pedido.telefone} leadOrigemId={pedido.lead_origem_id} />
          <TimelineCnpjButton cnpj={pedido.cnpj} />
        </div>
      </td>

      <td className="px-2 py-0 align-middle">
        <AcoesTriagem
          manter={
            <BotaoManter
              title={acoesBloqueadas && !isProcessing ? bloqueadoTitle : 'Manter — não dá para criar agora (HP/PRD/fora de carteira)? Justifique e passe para o fim da fila'}
              onClick={() => onManterFimFila(pedido)}
              disabled={acoesBloqueadas}
            />
          }
          avancar={
            <>
              {podePegar && (
                <Button
                  size="icon"
                  variant="ghost"
                  className={TRIAGEM_BTN_PRIMARIO}
                  title="Pegar — o pedido passa a ser seu e a contagem começa agora"
                  onClick={() => onPegar(pedido)}
                >
                  <Rocket className="h-3.5 w-3.5" />
                </Button>
              )}
              {semBiometria ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className={podePegar ? TRIAGEM_BTN : TRIAGEM_BTN_PRIMARIO}
                  title={acoesBloqueadas && !isProcessing ? bloqueadoTitle : 'Pedido criado — enviar para Input (fibra e CPF não têm biometria nem aceite)'}
                  onClick={() => onSemBiometria(pedido, 'INPUT')}
                  disabled={acoesBloqueadas}
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  variant="ghost"
                  className={podePegar ? TRIAGEM_BTN : TRIAGEM_BTN_PRIMARIO}
                  title={acoesBloqueadas && !isProcessing ? bloqueadoTitle : 'Pedido criado — enviar para Biometria (move todos os itens juntos)'}
                  onClick={() => onEnviarBiometria(pedido)}
                  disabled={acoesBloqueadas}
                >
                  <Fingerprint className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          }
          desvios={
            <>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className={TRIAGEM_BTN}
                title={!ehFibra
                  ? 'Aguardando HP é só para pedido de fibra'
                  : acoesBloqueadas && !isProcessing ? bloqueadoTitle : 'Não deu para criar — enviar para Aguardando HP'}
                onClick={() => onSemBiometria(pedido, 'AGUARDANDO HP')}
                disabled={acoesBloqueadas || !ehFibra}
              >
                <MapPinOff className="h-3.5 w-3.5" />
              </Button>
              {/* Vale para o pedido dela também: a supervisora pega para conferir e
                  precisa devolver. Antes o cadeado só aparecia no pedido dos outros. */}
              {podeLiberar && pedido.dono_id && (
                <Button
                  size="icon"
                  variant="ghost"
                  className={TRIAGEM_BTN}
                  title={isMeu
                    ? 'Liberar — tira o pedido do SEU nome e devolve para a fila livre'
                    : `Liberar — tira o pedido do nome de ${pedido.dono_nome || 'quem pegou'} e devolve para a fila livre`}
                  onClick={() => onLiberar(pedido)}
                  disabled={isProcessing}
                >
                  <Unlock className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          }
          ferramentas={
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN}
              title="Analisar — Abrir Painel do Pedido (Pedido 360)"
              onClick={() => openPedido(pedido.pedido_id, 'visao360')}
            >
              <Eye className="h-3 w-3" />
            </Button>
          }
          cancelar={
            <CancelarItemTriagemButton
              pedidoId={pedido.pedido_id}
              itens={pedido.itens}
              disabled={acoesBloqueadas}
              disabledTitle={bloqueadoTitle}
              onAfterCancel={onAfterCancel}
            />
          }
        />
      </td>
    </tr>
  );
});
